import { defineStore } from 'pinia';
import _ from 'lodash';
import Vue from 'vue';
import uniqid from 'uniqid';

import WizardApi from '@/store/api/cms/wizard';
import TicketApi from '@/extensions/tickets/store/api/ticket';
import pinia from '@/pinia';
import { useAuthStore } from '@/stores';

const defaultShop = {
    name: '',
    currency: 'EUR',
    currencySymbol: '€',
    defaultTaxRate: 19,
    reducedTaxRate: 7,
    customerGroupName: 'Standard',
    customerGroupKey: 'default',
    pointOfSaleName: '',
    pointOfSaleKey: '',
};

// Billing address fields - these go to cms.billingAddress (not stripe_billing_profiles!)
const defaultBillingAddress = {
    company: '',
    tax_id: '',
    gender: '',
    first_name: '',
    last_name: '',
    street: '',
    city: '',
    zipcode: '',
    country: 'DE',
    latitude: null,
    longitude: null,
};

const defaultBilling = {
    skipBilling: false,
};

const defaultBusinessCard = {
    description: '',
    category_id: null,
    phone: '',
    email: '',
    homepage_url: '',
    opening_hours: null,
    can_visit_location: false,
    has_delivery_service: false,
    image: null,
    address: null,
};

const defaultNewOwner = {
    email: '',
    first_name: '',
    last_name: '',
    company: '',
    address: {
        street: '',
        zipcode: '',
        city: '',
        country: 'DE',
        latitude: null,
        longitude: null,
    },
};

const defaultForm = {
    name: '',
    subdomain: '',
    domains: [],
    template: '',
    blueprint_id: null,
    owner_user_id: null,
    status: 'trial',
    acquisitionOwnerMode: 'existing',
    newOwner: _.cloneDeep(defaultNewOwner),
    i18n: false,
    languages: [],
    extension_keys: [],
    feature_keys: [],
    // named support pricing preset (config/tickets.php); 'basis' is the platform default
    support_tier: 'basis',
    // super-user wizard only: how we collect the money. 'invoice' = bank transfer,
    // the platform issues the invoice itself and optionally emails the PDF.
    billing_payment_method: 'stripe',
    billing_invoice_auto_send: false,
    apiKeys: [],
    smtp: null,
    newsletterSmtp: null,
    logo: null,
    shop: _.cloneDeep(defaultShop),
    shopMode: 'create',
    attach_to_shop_id: null,
    businessCard: _.cloneDeep(defaultBusinessCard),
    billing: _.cloneDeep(defaultBilling),
    billingAddress: _.cloneDeep(defaultBillingAddress),
    deep_clone: false,
};

export const useCmsWizardStore = defineStore('cmsWizard', {
    state: () => ({
        step: 1,
        validUntilStep: 1,
        loading: false,
        saving: false,
        form: _.cloneDeep(defaultForm),
        subdomainAvailable: null,
        checkingSubdomain: false,
        blueprints: [],
        templates: [],
        extensions: [],
        billingFeatures: [],
        // the prices are net; the price summary names the tax that goes on top
        taxRate: null,
        // named support pricing presets offered on the Setup step
        supportTiers: [],
        attachableShops: [],
        attachableShopsLoading: false,
        attachableShopsLoaded: false,
        // The picked owner option (id, label, email) survives step changes so
        // the remounted owner select can render the label instead of the raw id.
        selectedOwnerOption: null,
        selectedBlueprint: null,
        // Stripe billing state
        stripeSetupIntent: null,
        stripePaymentMethodId: null,
        stripePaymentComplete: false,
        stripeError: null,
        stripePaymentTouched: false,
        businessCardCategories: [],
        // Context mode: true when accessed via 'system-cms-wizard' route (creating CMS for someone else)
        superUserMode: false,
        // Update mode: true when updating an existing acquisition CMS (claim flow)
        updateMode: false,
        existingCmsId: null,
        // Created CMS reference for completion step (deep clone flow)
        createdCms: null,
        // Whether we're showing the completion step (deep clone in progress)
        showingCompletion: false,
    }),

    getters: {
        // Multistore attach mode is a shop flavor: the shop extension card is
        // unchecked (the store pays multistore-storefront instead), but every
        // shop-related wizard step still applies.
        shopEnabled: (state) => state.form.extension_keys.includes('shop') || state.form.shopMode === 'attach',
        businessCardEnabled: (state) => state.form.feature_keys.includes('business-card'),
        logo: (state) => state.form.logo,
        subdomain: (state) => state.form.subdomain,
        domains: (state) => state.form.domains,
        languages: (state) => state.form.languages,
        // Filter extensions by selected template's supported extensions (plus system-wide extensions)
        availableExtensions: (state) => {
            const selectedTemplate = state.templates.find((t) => t.id === state.form.template);
            const supportedExtensions = _.get(selectedTemplate, 'extensions', []);
            if (!supportedExtensions.length) {
                return state.extensions;
            }
            return state.extensions.filter((ext) => {
                // A super-user-only feature is assigned by us on the Features
                // screen and never offered here, whatever else is set on it.
                if (_.get(ext, 'billingFeature.super_user_only', false)) {
                    return false;
                }
                // Include if template supports it
                if (supportedExtensions.includes(ext.key)) {
                    return true;
                }
                // Include if system-wide (always available)
                const isSystemWide = _.get(ext, 'billingFeature.system_wide', false);
                return isSystemWide;
            });
        },
        // Calculate total steps dynamically based on configuration and mode
        // Super user mode: 1. Blueprint, 2. Setup, 3. ShopConfig (if shop), 4. API Keys
        // Non-super user mode: 1. Blueprint, 2. Setup, 3. ShopConfig (if shop), 4. Billing, 5. API Keys
        totalSteps: (state) => {
            let steps = 3; // Blueprint, Setup, API Keys
            if (state.form.feature_keys.includes('business-card')) {
                steps += 1; // BusinessCardConfig
            }
            if (state.form.extension_keys.includes('shop') || state.form.shopMode === 'attach') {
                steps += 1; // ShopConfig (attach mode is a shop flavor without the shop card checked)
            }
            if (!state.superUserMode && !state.updateMode) {
                steps += 1; // Billing (only in non-super user, non-update mode)
            }
            return steps;
        },
        // Check if the current user is a super user
        isSuperUser: () => {
            const authStore = useAuthStore(pinia);
            return authStore && authStore.user && authStore.user.super_user;
        },
        // Show billing step when not in super-user mode and not in update mode
        showBillingStep: (state) => !state.superUserMode && !state.updateMode,
        // Check if completion step should be shown (after deep clone CMS creation)
        shouldShowCompletionStep: (state) => state.showingCompletion && state.createdCms !== null,
        // Get billing feature by key
        getBillingFeature: (state) => (key) => {
            return state.billingFeatures.find((feature) => feature.key === key);
        },
        // Get extension by key
        getExtension: (state) => (key) => {
            return state.extensions.find((ext) => ext.key === key);
        },
        // Get price summary items for billing step display.
        // Each cost on a billing feature falls into one of three buckets that drive the UI:
        //   - recurring (billing_type='recurring' or legacy cost_type='default'): always-billed monthly fee → counts toward monthly total
        //   - included quota (billing_type='per-unit' AND bill_minimum_batch=true): one batch is always billed even at 0 usage → counts toward monthly total
        //   - pure usage (billing_type='per-unit' AND bill_minimum_batch=false): only billed on use → reported as "additional usage" only
        //
        // The store emits structural data only — no German strings, no pre-formatted badges.
        // Components translate cost_type via i18n keys ('cost_type.{key}') and unit_label via 'unit.{label}'.
        // Each item carries:
        //   key, isPerUnit, price          — required
        //   parentName                     — translated/labeled feature name (used for the recurring/base row)
        //   costType                       — present when this row represents a specific BillingFeatureCost (overage, included, rate)
        //   batchSize, unitLabel           — present when the row should display a batch-rate badge
        //   kind                           — 'recurring' | 'included' | 'overage' | 'rate'
        priceSummaryItems: (state) => {
            const getCosts = (feature) => _.get(feature, 'costs', []);
            const getRecurringCost = (feature) =>
                getCosts(feature).find((cost) => cost.billing_type === 'recurring' || cost.cost_type === 'default') ||
                null;
            const getMinimumBatchCosts = (feature) =>
                getCosts(feature).filter(
                    (cost) => cost.billing_type === 'per-unit' && _.get(cost, 'bill_minimum_batch', false)
                );
            const getPureUsageCosts = (feature) =>
                getCosts(feature).filter(
                    (cost) => cost.billing_type === 'per-unit' && !_.get(cost, 'bill_minimum_batch', false)
                );

            const items = [];
            const seen = {};
            const pushItem = (item) => {
                if (seen[item.key]) return;
                seen[item.key] = true;
                items.push(item);
            };

            // recurringPriceOverride: the service fee is whatever the chosen
            // support tier costs, not the feature's stored platform default.
            const addFeatureItems = (feature, parentName, recurringPriceOverride) => {
                if (!feature) return;
                const recurring = getRecurringCost(feature);
                const recurringPrice =
                    recurringPriceOverride === undefined
                        ? parseFloat(_.get(recurring, 'price', 0)) || 0
                        : recurringPriceOverride;
                if (recurring && recurringPrice > 0) {
                    pushItem({
                        key: feature.key + ':base',
                        kind: 'recurring',
                        parentName: parentName,
                        price: recurringPrice,
                        isPerUnit: false,
                    });
                }
                getMinimumBatchCosts(feature).forEach((cost) => {
                    const price = parseFloat(cost.price) || 0;
                    if (price <= 0) return;
                    const batchSize = parseInt(_.get(cost, 'batch_size', 1), 10) || 1;
                    const unitLabel = _.get(cost, 'unit_label', '');
                    pushItem({
                        key: feature.key + ':' + cost.cost_type + ':included',
                        kind: 'included',
                        parentName: parentName,
                        parentKey: feature.key,
                        costType: cost.cost_type,
                        costLabel: _.get(cost, 'label', ''),
                        batchSize: batchSize,
                        unitLabel: unitLabel,
                        price: price,
                        isPerUnit: false,
                    });
                    pushItem({
                        key: feature.key + ':' + cost.cost_type + ':overage',
                        kind: 'overage',
                        parentName: parentName,
                        parentKey: feature.key,
                        costType: cost.cost_type,
                        costLabel: _.get(cost, 'label', ''),
                        batchSize: batchSize,
                        unitLabel: unitLabel,
                        price: price,
                        isPerUnit: true,
                        isMetered: !!cost.metered_actual_cost,
                    });
                });
                getPureUsageCosts(feature).forEach((cost) => {
                    const price = parseFloat(cost.price) || 0;
                    if (price <= 0) return;
                    const batchSize = parseInt(_.get(cost, 'batch_size', 1), 10) || 1;
                    const unitLabel = _.get(cost, 'unit_label', '');
                    pushItem({
                        key: feature.key + ':' + cost.cost_type,
                        kind: 'rate',
                        parentName: parentName,
                        parentKey: feature.key,
                        costType: cost.cost_type,
                        costLabel: _.get(cost, 'label', ''),
                        batchSize: batchSize,
                        unitLabel: unitLabel,
                        price: price,
                        isPerUnit: true,
                        isMetered: !!cost.metered_actual_cost,
                    });
                });
            };

            // Base features (type='base') are always included
            state.billingFeatures
                .filter((f) => f.type === 'base')
                .forEach((feature) => {
                    const tier =
                        feature.key === 'service-fee'
                            ? state.supportTiers.find((entry) => entry.key === state.form.support_tier)
                            : null;
                    addFeatureItems(
                        feature,
                        feature.label || feature.key,
                        tier ? parseFloat(tier.service_fee) || 0 : undefined
                    );

                    // The tier's included minutes are a quota like the newsletter's
                    // included emails, so they get the same "… inkl." line. They cost
                    // nothing on top of the fee, hence no price.
                    const includedMinutes = tier ? parseInt(tier.included_minutes, 10) || 0 : 0;
                    if (includedMinutes > 0) {
                        const supportTimeCost = getCosts(feature).find((cost) => cost.cost_type === 'support-time');
                        pushItem({
                            key: feature.key + ':support-time:included',
                            kind: 'included',
                            parentName: feature.label || feature.key,
                            parentKey: feature.key,
                            costType: 'support-time',
                            costLabel: _.get(supportTimeCost, 'label', ''),
                            batchSize: includedMinutes,
                            unitLabel: _.get(supportTimeCost, 'unit_label', 'minutes'),
                            price: null,
                            isPerUnit: false,
                        });
                    }
                });

            // Selected extensions
            state.form.extension_keys.forEach((key) => {
                const extension = state.extensions.find((ext) => ext.key === key);
                if (!extension) return;
                const billingFeature = _.get(extension, 'billingFeature');
                if (!billingFeature) return;
                const includedInKey = billingFeature.included_in_extension_key;
                if (includedInKey && state.form.extension_keys.includes(includedInKey)) {
                    return;
                }
                // The summary items are deduplicated by feature key: without one,
                // every extension would collapse into a single "undefined" item
                // and all but the first selected extension would disappear.
                addFeatureItems(
                    { key: extension.key, ...billingFeature },
                    billingFeature.label || extension.name || extension.key
                );
            });

            // Multistore: an attached store pays the multistore-storefront
            // rate; the shop extension card is unchecked in that mode, so the
            // extension loop above never reaches it.
            if (state.form.shopMode === 'attach') {
                const multistoreFeature = state.billingFeatures.find(
                    (feature) => feature.key === 'multistore-storefront'
                );
                if (multistoreFeature) {
                    addFeatureItems(multistoreFeature, multistoreFeature.label || multistoreFeature.key);
                }
            }

            // Selected standalone billing features (e.g. business-card, TSE)
            state.form.feature_keys.forEach((key) => {
                const feature = state.billingFeatures.find((f) => f.key === key);
                if (!feature) return;
                addFeatureItems(feature, feature.label || feature.key);
            });

            return items;
        },
        // Calculate the displayed monthly minimum: recurring fees + sum of all bill_minimum_batch prices.
        // Pure per-unit costs (no minimum batch) are excluded — they are reported separately as "additional usage".
        totalMonthlyPrice: (state) => {
            const getCosts = (feature) => _.get(feature, 'costs', []);
            const getRecurringCost = (feature) =>
                getCosts(feature).find((cost) => cost.billing_type === 'recurring' || cost.cost_type === 'default') ||
                null;
            const getRecurringPrice = (feature) => parseFloat(_.get(getRecurringCost(feature), 'price', 0)) || 0;
            const getMinimumBatchTotal = (feature) =>
                getCosts(feature)
                    .filter((cost) => cost.billing_type === 'per-unit' && _.get(cost, 'bill_minimum_batch', false))
                    .reduce((sum, cost) => sum + (parseFloat(cost.price) || 0), 0);
            const getFeatureMinimum = (feature) => getRecurringPrice(feature) + getMinimumBatchTotal(feature);

            let total = 0;

            state.billingFeatures
                .filter((f) => f.type === 'base')
                .forEach((feature) => {
                    // The service fee is whatever the chosen support tier costs; the
                    // feature's own price is only the platform default (Basis).
                    if (feature.key === 'service-fee') {
                        const tier = state.supportTiers.find((entry) => entry.key === state.form.support_tier);
                        total += tier ? parseFloat(tier.service_fee) || 0 : getFeatureMinimum(feature);
                        return;
                    }
                    total += getFeatureMinimum(feature);
                });

            state.form.extension_keys.forEach((key) => {
                const extension = state.extensions.find((ext) => ext.key === key);
                if (!extension) return;
                const billingFeature = _.get(extension, 'billingFeature');
                if (!billingFeature) return;
                const includedInKey = billingFeature.included_in_extension_key;
                if (includedInKey && state.form.extension_keys.includes(includedInKey)) {
                    return;
                }

                total += getFeatureMinimum(billingFeature);
            });

            // Multistore: an attached store pays the multistore-storefront
            // rate; the shop extension card is unchecked in that mode, so its
            // price never enters the loop above.
            if (state.form.shopMode === 'attach') {
                const multistoreFeature = state.billingFeatures.find(
                    (feature) => feature.key === 'multistore-storefront'
                );
                if (multistoreFeature) {
                    total += getFeatureMinimum(multistoreFeature);
                }
            }

            state.form.feature_keys.forEach((key) => {
                const feature = state.billingFeatures.find((f) => f.key === key);
                if (!feature) return;
                total += getFeatureMinimum(feature);
            });

            return total;
        },
        // True when any selected feature/extension exposes pay-per-use costs on top of its recurring minimum.
        // Drives the "ab X €" (German "from X €") prefix on the displayed monthly total.
        hasAnyUsageCosts() {
            return (this.priceSummaryItems || []).some((item) => item.isPerUnit);
        },
        // Multistore: a satellite must belong to the same owner as the shop it
        // attaches to, so only the effective owner's shops are attachable. The
        // effective owner mirrors the payload logic: the explicitly chosen
        // owner, or the authenticated user when none was picked.
        ownerScopedAttachableShops: (state) => {
            const authStore = useAuthStore(pinia);
            const ownerUserId = parseInt(state.form.owner_user_id || _.get(authStore, 'user.id')) || null;
            if (!ownerUserId) return [];

            return state.attachableShops.filter((shop) => {
                const shopOwnerUserId = parseInt(_.get(shop, 'ownerCms.owner.user.id'));
                return shopOwnerUserId === ownerUserId;
            });
        },
    },

    actions: {
        setSuperUserMode(value) {
            this.superUserMode = value;
        },

        setSelectedOwnerOption(option) {
            this.selectedOwnerOption = option || null;
        },

        toggleMultistoreMode() {
            if (this.form.shopMode === 'attach') {
                this.form.shopMode = 'create';
                this.form.attach_to_shop_id = null;
                return;
            }

            // Selecting multistore replaces a standalone shop: drop the shop
            // extension (cascades POS and TSE add-ons away) before switching
            // the mode — toggleExtension resets shopMode, so set it after.
            if (this.form.extension_keys.includes('shop')) {
                this.toggleExtension('shop');
            }

            // The satellite runs on the shared shop, so extensions included in
            // a shop (e.g. the restricted area) come with it and must not be
            // billed separately — same rule as enabling the shop extension.
            this.extensions.forEach((extension) => {
                if (_.get(extension, 'billingFeature.included_in_extension_key') !== 'shop') return;

                const includedIndex = this.form.extension_keys.indexOf(extension.key);
                if (includedIndex > -1) {
                    this.form.extension_keys.splice(includedIndex, 1);
                }
            });

            this.form.shopMode = 'attach';
        },

        loadAttachableShops() {
            if (this.attachableShopsLoaded || this.attachableShopsLoading) {
                return Promise.resolve(this.attachableShops);
            }

            this.attachableShopsLoading = true;
            return WizardApi.fetchAttachableShops()
                .then((shops) => {
                    this.attachableShops = shops || [];
                    this.attachableShopsLoaded = true;
                    return this.attachableShops;
                })
                .finally(() => {
                    this.attachableShopsLoading = false;
                });
        },

        setStep(step) {
            this.step = step;
        },

        setValidUntilStep(step) {
            this.validUntilStep = step;
        },

        setLoading(loading) {
            this.loading = loading;
        },

        setSaving(saving) {
            this.saving = saving;
        },

        updateForm(payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(this.form, key)) {
                    this.form[key] = payload[key];
                }
            });
        },

        updateShop(payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(this.form.shop, key)) {
                    this.form.shop[key] = payload[key];
                }
            });
        },

        updateBilling(payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(this.form.billing, key)) {
                    this.form.billing[key] = payload[key];
                }
            });
        },

        updateBillingAddress(payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(this.form.billingAddress, key)) {
                    this.form.billingAddress[key] = payload[key];
                }
            });
        },

        setBlueprints(blueprints) {
            this.blueprints = blueprints;
        },

        setTemplates(templates) {
            this.templates = templates;
        },

        setExtensions(extensions) {
            this.extensions = extensions;
        },

        setBillingFeatures(billingFeatures) {
            this.billingFeatures = billingFeatures;
        },

        setSelectedBlueprint(blueprint) {
            this.selectedBlueprint = blueprint;
        },

        toggleFeature(featureKey) {
            const index = this.form.feature_keys.indexOf(featureKey);
            if (index > -1) {
                this.form.feature_keys.splice(index, 1);
                return;
            }

            if (!this.featureRequirementsMet(featureKey)) {
                return;
            }

            this.form.feature_keys.push(featureKey);
        },

        // A feature hangs under ONE card but can NEED other extensions: WhatsApp
        // order notifications are sent through Kundenkommunikation and are
        // pointless without a shop, because without orders there is nothing to
        // notify about.
        featureRequirementsMet(featureKey) {
            const feature = this.billingFeatures.find((billingFeature) => billingFeature.key === featureKey);
            const required = _.get(feature, 'requires_extension_keys') || [];

            return required.every((extensionKey) => this.form.extension_keys.includes(extensionKey));
        },

        updateNewOwner(payload) {
            Object.keys(payload).forEach((key) => {
                if (Object.prototype.hasOwnProperty.call(this.form.newOwner, key)) {
                    // this.form.newOwner[key] = payload[key];
                    Vue.set(this.form.newOwner, key, payload[key]);
                }
            });
        },

        updateBusinessCard(payload) {
            Object.keys(payload).forEach((key) => {
                Vue.set(this.form.businessCard, key, payload[key]);
            });
        },

        setBusinessCardImage(image) {
            Vue.set(this.form.businessCard, 'image', image);
        },

        removeBusinessCardImage() {
            Vue.set(this.form.businessCard, 'image', null);
        },

        updateBusinessCardImage(data) {
            if (!this.form.businessCard.image) return;
            if (data.settings !== undefined) {
                Vue.set(this.form.businessCard.image, 'settings', data.settings);
            }
            if (data.title !== undefined) {
                Vue.set(this.form.businessCard.image, 'title', data.title);
            }
            if (data.alt !== undefined) {
                Vue.set(this.form.businessCard.image, 'alt', data.alt);
            }
        },

        setBusinessCardAddress(address) {
            Vue.set(this.form.businessCard, 'address', address);
        },

        removeBusinessCardAddress() {
            Vue.set(this.form.businessCard, 'address', null);
        },

        setBusinessCardCategories(categories) {
            this.businessCardCategories = categories;
        },

        toggleExtension(extensionKey) {
            const index = this.form.extension_keys.indexOf(extensionKey);
            if (index > -1) {
                // Disabling extension — collect the transitive closure of every extension
                // that should fall away (e.g. deselecting Shop must also drop POS, which
                // must also drop TSE add-on). Without this, add-on features registered
                // against descendant extensions stay in feature_keys and inflate the total.
                const removedExtensionKeys = [extensionKey];
                let frontier = [extensionKey];
                while (frontier.length > 0) {
                    const nextFrontier = [];
                    this.extensions.forEach((ext) => {
                        const dependsOnKey = _.get(ext, 'billingFeature.depends_on_extension_key');
                        if (frontier.includes(dependsOnKey) && !removedExtensionKeys.includes(ext.key)) {
                            removedExtensionKeys.push(ext.key);
                            nextFrontier.push(ext.key);
                        }
                    });
                    frontier = nextFrontier;
                }

                // Drop all collected extension keys from the form
                removedExtensionKeys.forEach((key) => {
                    const keyIndex = this.form.extension_keys.indexOf(key);
                    if (keyIndex > -1) {
                        this.form.extension_keys.splice(keyIndex, 1);
                    }
                });

                // Drop every add-on billing feature that depends on any of the removed extensions
                this.billingFeatures.forEach((feature) => {
                    if (removedExtensionKeys.includes(feature.depends_on_extension_key)) {
                        const featureIndex = this.form.feature_keys.indexOf(feature.key);
                        if (featureIndex > -1) {
                            this.form.feature_keys.splice(featureIndex, 1);
                        }
                    }
                });

                // Features that merely REQUIRE one of the removed extensions
                // fall away too, even though they hang under another card.
                this.billingFeatures.forEach((feature) => {
                    const required = _.get(feature, 'requires_extension_keys') || [];

                    if (required.some((key) => removedExtensionKeys.includes(key))) {
                        const featureIndex = this.form.feature_keys.indexOf(feature.key);
                        if (featureIndex > -1) {
                            this.form.feature_keys.splice(featureIndex, 1);
                        }
                    }
                });

                // Multistore: without the shop extension there is nothing to
                // attach — fall back to the create mode.
                if (removedExtensionKeys.includes('shop') && this.form.shopMode === 'attach') {
                    this.form.shopMode = 'create';
                    this.form.attach_to_shop_id = null;
                }
            } else {
                // Enabling extension
                this.form.extension_keys.push(extensionKey);

                // Auto-disable extensions that are "included in" this one
                this.extensions.forEach((ext) => {
                    const includedInKey = _.get(ext, 'billingFeature.included_in_extension_key');
                    if (includedInKey === extensionKey) {
                        const includedIndex = this.form.extension_keys.indexOf(ext.key);
                        if (includedIndex > -1) {
                            this.form.extension_keys.splice(includedIndex, 1);
                        }
                    }
                });
            }
        },

        setLogo(logo) {
            this.form.logo = logo;
        },

        removeLogo() {
            this.form.logo = null;
        },

        setSubdomainAvailable(available) {
            this.subdomainAvailable = available;
        },

        setCheckingSubdomain(checking) {
            this.checkingSubdomain = checking;
        },

        addDomain() {
            this.form.domains.push({ base_url: '' });
        },

        updateDomain({ index, value }) {
            if (this.form.domains[index]) {
                this.form.domains[index].base_url = value;
            }
        },

        removeDomain(index) {
            this.form.domains.splice(index, 1);
        },

        setLanguages(languages) {
            this.form.languages = languages;
        },

        // API Keys mutations (for reusable components)
        cmsApiKey({ index, field, value }) {
            Vue.set(this.form.apiKeys[index], field, value);
        },

        addCmsApiKey(key) {
            const index = _.get(this, 'form.apiKeys.length', 0);
            if (this.form.apiKeys === undefined) {
                Vue.set(this.form, 'apiKeys', []);
            }
            Vue.set(this.form.apiKeys, index, {
                id: uniqid('new:'),
                key,
                value: '',
                public: '',
                private: '',
            });
        },

        // Regular SMTP mutations (for SmtpCredentials component)
        smtpHost(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'host', value);
        },

        smtpPort(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'port', value);
        },

        smtpUsername(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'username', value);
        },

        smtpPassword(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'password', value);
        },

        smtpAddress(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'address', value);
        },

        smtpName(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'name', value);
        },

        smtpMailgunWebhookSigningKey(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'mailgun_webhook_signing_key', value);
        },

        smtpAuthMethod(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'auth_method', value);
        },

        // The one-time OAuth nonce from the consent popup; the parked tokens link
        // onto the transactional channel when the new CMS is saved (link-on-save).
        smtpOauthPendingToken(value) {
            if (!this.form.smtp) Vue.set(this.form, 'smtp', { id: uniqid('new:') });
            Vue.set(this.form.smtp, 'oauth_pending_token', value);
        },

        // Newsletter SMTP mutations (for SmtpCredentials component)
        newsletterSmtpHost(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'host', value);
        },

        newsletterSmtpPort(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'port', value);
        },

        newsletterSmtpUsername(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'username', value);
        },

        newsletterSmtpPassword(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'password', value);
        },

        newsletterSmtpAddress(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'address', value);
        },

        newsletterSmtpName(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'name', value);
        },

        newsletterSmtpMailgunWebhookSigningKey(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'mailgun_webhook_signing_key', value);
        },

        newsletterSmtpAuthMethod(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'auth_method', value);
        },

        newsletterSmtpOauthPendingToken(value) {
            if (!this.form.newsletterSmtp) Vue.set(this.form, 'newsletterSmtp', { id: uniqid('new:') });
            Vue.set(this.form.newsletterSmtp, 'oauth_pending_token', value);
        },

        resetForm() {
            this.step = 1;
            this.validUntilStep = 1;
            this.form = _.cloneDeep(defaultForm);
            this.subdomainAvailable = null;
            this.checkingSubdomain = false;
            this.selectedBlueprint = null;
            this.selectedOwnerOption = null;
            this.superUserMode = false;
            // Reset update mode state
            this.updateMode = false;
            this.existingCmsId = null;
            // Reset Stripe state
            this.stripeSetupIntent = null;
            this.stripePaymentMethodId = null;
            this.stripePaymentComplete = false;
            this.stripeError = null;
            this.stripePaymentTouched = false;
            // Reset deep clone completion state
            this.createdCms = null;
            this.showingCompletion = false;
        },

        // Stripe billing actions
        setStripeSetupIntent(setupIntent) {
            this.stripeSetupIntent = setupIntent;
        },

        setStripePaymentMethodId(paymentMethodId) {
            this.stripePaymentMethodId = paymentMethodId;
        },

        setStripePaymentComplete(complete) {
            this.stripePaymentComplete = complete;
        },

        setStripeError(error) {
            this.stripeError = error;
        },

        setStripePaymentTouched(touched) {
            this.stripePaymentTouched = touched;
        },

        setCreatedCms(cms) {
            this.createdCms = cms;
        },

        setShowingCompletion(showing) {
            this.showingCompletion = showing;
        },

        async createSetupIntent() {
            try {
                const result = await WizardApi.createWizardStripeSetupIntent();
                this.setStripeSetupIntent(result);
                return result;
            } catch (error) {
                console.error('Failed to create setup intent:', error);
                throw error;
            }
        },

        // Async actions
        enterUpdateMode(existingCms) {
            this.updateMode = true;
            this.existingCmsId = parseInt(existingCms.id);

            // Pre-fill basic CMS info
            this.form.name = existingCms.name || '';
            this.form.i18n = existingCms.i18n || false;

            // Pre-fill template from existing config
            const templateId = _.get(existingCms, 'config.template.id');
            if (templateId) {
                this.form.template = templateId;
            }

            // Pre-fill extensions
            const extensions = _.get(existingCms, 'extensions', []);
            this.form.extension_keys = extensions.map(function (extension) {
                return extension.key;
            });

            // Pre-fill domains: extract subdomain from *.cityoffer.online, keep the rest as additional
            var domains = _.get(existingCms, 'domains', []);
            var cityofferDomain = _.find(domains, function (domain) {
                return _.get(domain, 'base_url', '').endsWith('.cityoffer.online');
            });
            if (cityofferDomain) {
                this.form.subdomain = cityofferDomain.base_url.replace('.cityoffer.online', '');
            }
            this.form.domains = domains
                .filter(function (domain) {
                    return !_.get(domain, 'base_url', '').endsWith('.cityoffer.online');
                })
                .map(function (domain) {
                    return { base_url: domain.base_url };
                });

            // Pre-fill languages
            var languages = _.get(existingCms, 'languages', []);
            this.form.languages = languages.map(function (language) {
                return {
                    code: language.code,
                    default: language.default,
                    status: language.status,
                };
            });

            // Pre-fill feature keys based on existing CMS data
            var businessCard = _.get(existingCms, 'businessCard');
            if (businessCard || _.get(existingCms, 'is_business_card')) {
                if (!this.form.feature_keys.includes('business-card')) {
                    this.form.feature_keys.push('business-card');
                }
            }

            // Pre-fill business card data
            if (businessCard) {
                this.form.businessCard = {
                    description: businessCard.description || '',
                    category_id: _.get(businessCard, 'category.id', null),
                    phone: businessCard.phone || '',
                    email: businessCard.email || '',
                    homepage_url: businessCard.homepage_url || '',
                    opening_hours: businessCard.opening_hours || null,
                    can_visit_location: businessCard.can_visit_location || false,
                    has_delivery_service: businessCard.has_delivery_service || false,
                    image: businessCard.image || null,
                    address: businessCard.address || null,
                };
            }

            // Pre-fill billing address
            var billingAddress = _.get(existingCms, 'billingAddress');
            if (billingAddress) {
                this.form.billingAddress = {
                    company: billingAddress.company || '',
                    tax_id: billingAddress.tax_id || '',
                    gender: billingAddress.gender || '',
                    first_name: billingAddress.first_name || '',
                    last_name: billingAddress.last_name || '',
                    street: billingAddress.street || '',
                    city: billingAddress.city || '',
                    zipcode: billingAddress.zipcode || '',
                    country: billingAddress.country || 'DE',
                    latitude: billingAddress.latitude || null,
                    longitude: billingAddress.longitude || null,
                };
            }

            // Pre-fill logo
            var logo = _.find(_.get(existingCms, 'images', []), function (image) {
                return image.type === 'cms-logo';
            });
            if (logo) {
                this.form.logo = logo;
            }

            // Pre-fill API keys
            var apiKeys = _.get(existingCms, 'apiKeys', []);
            if (apiKeys.length) {
                this.form.apiKeys = apiKeys.map(function (apiKey) {
                    return {
                        id: apiKey.id,
                        key: apiKey.key,
                        name: apiKey.name,
                        value: apiKey.value,
                        public: apiKey.public,
                        private: apiKey.private,
                    };
                });
            }

            // Pre-fill SMTP
            var smtp = _.get(existingCms, 'smtp');
            if (smtp) {
                this.form.smtp = {
                    id: smtp.id,
                    type: smtp.type || '',
                    host: smtp.host || '',
                    port: smtp.port || '',
                    username: smtp.username || '',
                    password: smtp.password || '',
                    address: smtp.address || '',
                    name: smtp.name || '',
                };
            }

            // Pre-fill newsletter SMTP
            var newsletterSmtp = _.get(existingCms, 'newsletterSmtp');
            if (newsletterSmtp) {
                this.form.newsletterSmtp = {
                    id: newsletterSmtp.id,
                    type: newsletterSmtp.type || '',
                    host: newsletterSmtp.host || '',
                    port: newsletterSmtp.port || '',
                    username: newsletterSmtp.username || '',
                    password: newsletterSmtp.password || '',
                    address: newsletterSmtp.address || '',
                    name: newsletterSmtp.name || '',
                };
            }
        },

        async fetchInitialData() {
            this.setLoading(true);
            try {
                const [blueprints, templates, extensions, billingFeatures, taxRate, businessCardCategories, supportTiers] =
                    await Promise.all([
                        WizardApi.fetchBlueprints(),
                        WizardApi.fetchTemplates(),
                        WizardApi.fetchExtensions(),
                        WizardApi.fetchBillingFeatures(),
                        WizardApi.fetchBillingTaxRate(),
                        WizardApi.fetchBusinessCardCategories(),
                        // the support tiers are a super-user-only query; a customer
                        // signing up gets the platform default rather than an error
                        TicketApi.fetchSupportTiers().catch(() => []),
                    ]);

                this.setBlueprints(blueprints || []);
                this.setTemplates(templates || []);
                this.setExtensions(extensions || []);
                this.setBillingFeatures(billingFeatures || []);
                this.taxRate = taxRate;
                this.setBusinessCardCategories(businessCardCategories || []);
                this.supportTiers = supportTiers || [];
            } catch (error) {
                console.error('Failed to fetch wizard data:', error);
                throw error;
            } finally {
                this.setLoading(false);
            }
        },

        selectBlueprint(blueprintId) {
            const blueprint = this.blueprints.find((b) => b.id === blueprintId);
            this.setSelectedBlueprint(blueprint || null);

            if (blueprint) {
                // Apply blueprint's template only - extensions are selected in step 3
                const templateId = _.get(blueprint, 'config.template.id');

                this.updateForm({
                    blueprint_id: blueprintId,
                    template: templateId || '',
                });
            } else {
                this.updateForm({
                    blueprint_id: null,
                    template: '',
                    extension_keys: [],
                    deep_clone: false,
                });
                // Reset validation - can't proceed past current step without a selection
                this.setValidUntilStep(this.step);
            }
        },

        nextStep() {
            const newStep = this.step + 1;
            this.setStep(newStep);
            if (newStep > this.validUntilStep) {
                this.setValidUntilStep(newStep);
            }
        },

        prevStep() {
            if (this.step > 1) {
                this.setStep(this.step - 1);
            }
        },

        goToStep(step) {
            if (step <= this.validUntilStep) {
                this.setStep(step);
            }
        },

        async checkSubdomainAvailability() {
            const subdomain = this.form.subdomain;
            if (!subdomain || subdomain.length < 3) {
                this.setSubdomainAvailable(null);
                return;
            }

            this.setCheckingSubdomain(true);
            try {
                const isAvailable = await WizardApi.checkSubdomainAvailability(subdomain);
                this.setSubdomainAvailable(isAvailable);
            } catch (error) {
                console.error('Failed to check subdomain availability:', error);
                this.setSubdomainAvailable(null);
            } finally {
                this.setCheckingSubdomain(false);
            }
        },

        async searchOwners(query, options) {
            try {
                const results = await WizardApi.searchOwners(query, options);
                return results;
            } catch (error) {
                console.error('Failed to search owners:', error);
                throw error;
            }
        },

        async initializeCms() {
            this.setSaving(true);
            try {
                // Use explicitly selected owner, or fall back to authenticated user.
                // A draft has no billing party yet and is allowed to stay ownerless, so it
                // never falls back - otherwise the super-user creating it becomes its owner.
                const authStore = useAuthStore(pinia);
                const isDraft = this.superUserMode && this.form.status === 'draft';
                const ownerUserId = this.form.owner_user_id || (isDraft ? null : authStore.user.id);

                // Build the cms input object
                const cmsInput = {
                    name: this.form.name,
                    i18n: this.form.i18n,
                    billing_period: 'monthly',
                    languages: this.form.languages,
                    domain: this.form.subdomain,
                    domains: this.form.domains.filter((d) => d.base_url),
                    config: {
                        template: this.form.template,
                    },
                    // Multistore: a satellite needs the shop extension even
                    // though the shop card is unchecked in attach mode (the
                    // store pays multistore-storefront instead).
                    enabledExtensionKeys:
                        this.form.shopMode === 'attach' && !this.form.extension_keys.includes('shop')
                            ? [...this.form.extension_keys, 'shop']
                            : this.form.extension_keys,
                };

                // Pass logo through images array (same pattern as SaveCms)
                if (this.form.logo) {
                    cmsInput.images = cmsInput.images || [];
                    cmsInput.images.push({
                        type: 'cms-logo',
                        upload: true,
                        id: this.form.logo.id,
                    });
                }

                // Include shop config if shop extension is enabled — unless the
                // new CMS attaches to an existing shop as a multistore satellite
                // (the shared shop already carries all defaults).
                if (this.form.extension_keys.includes('shop') && this.form.shopMode !== 'attach') {
                    cmsInput.shop = {
                        name: this.form.shop.name,
                        currencies: [
                            {
                                iso4217: this.form.shop.currency,
                                sign: this.form.shop.currencySymbol,
                            },
                        ],
                        defaultTax: {
                            value: this.form.shop.defaultTaxRate,
                        },
                        defaultReducedTax: {
                            value: this.form.shop.reducedTaxRate,
                        },
                        defaultCustomerGroup: {
                            name: this.form.shop.customerGroupName,
                            key: this.form.shop.customerGroupKey,
                        },
                        pointsOfSale: this.form.shop.pointOfSaleName
                            ? [{ name: this.form.shop.pointOfSaleName, key: this.form.shop.pointOfSaleKey }]
                            : undefined,
                    };
                }

                // Include API keys
                const apiKeys = this.form.apiKeys || [];
                if (apiKeys.length > 0) {
                    cmsInput.apiKeys = apiKeys.map((k) => ({
                        key: k.key,
                        name: k.name || k.key,
                        public: k.public || null,
                        private: k.private || null,
                        value: k.value || null,
                    }));
                }

                // Include regular SMTP config if any email-sending extension is enabled
                const emailExtensions = ['shop', 'forms', 'restricted-area', 'event-manager', 'newsletter'];
                const hasEmailExtension = emailExtensions.some((ext) => this.form.extension_keys.includes(ext));
                if (hasEmailExtension && this.form.smtp) {
                    cmsInput.smtp = {
                        address: this.form.smtp.address || null,
                        name: this.form.smtp.name || null,
                        host: this.form.smtp.host || null,
                        port: this.form.smtp.port || null,
                        username: this.form.smtp.username || null,
                        password: this.form.smtp.password || null,
                        mailgun_webhook_signing_key: this.form.smtp.mailgun_webhook_signing_key || null,
                        // OAuth: the chosen provider and the one-time consent nonce, so the
                        // parked tokens link onto the new transactional channel on creation.
                        auth_method: this.form.smtp.auth_method || null,
                        oauth_pending_token: this.form.smtp.oauth_pending_token || null,
                    };
                }

                // Include newsletter SMTP config if newsletter extension is enabled
                if (this.form.extension_keys.includes('newsletter') && this.form.newsletterSmtp) {
                    cmsInput.newsletterSmtp = {
                        address: this.form.newsletterSmtp.address || null,
                        name: this.form.newsletterSmtp.name || null,
                        host: this.form.newsletterSmtp.host || null,
                        port: this.form.newsletterSmtp.port || null,
                        username: this.form.newsletterSmtp.username || null,
                        password: this.form.newsletterSmtp.password || null,
                        mailgun_webhook_signing_key: this.form.newsletterSmtp.mailgun_webhook_signing_key || null,
                        auth_method: this.form.newsletterSmtp.auth_method || null,
                        oauth_pending_token: this.form.newsletterSmtp.oauth_pending_token || null,
                    };
                }

                // Include billing address as part of CMS (goes to cms.billingAddress, NOT stripe_billing_profiles!)
                if (!this.form.billing.skipBilling) {
                    const billingAddress = this.form.billingAddress;
                    // Only include if any field has a value
                    const hasData =
                        billingAddress.company ||
                        billingAddress.tax_id ||
                        billingAddress.first_name ||
                        billingAddress.last_name ||
                        billingAddress.street ||
                        billingAddress.city ||
                        billingAddress.zipcode;

                    if (hasData) {
                        cmsInput.billingAddress = {
                            company: billingAddress.company || null,
                            tax_id: billingAddress.tax_id || null,
                            gender: billingAddress.gender || null,
                            first_name: billingAddress.first_name || null,
                            last_name: billingAddress.last_name || null,
                            street: billingAddress.street || null,
                            city: billingAddress.city || null,
                            zipcode: billingAddress.zipcode || null,
                            country: billingAddress.country || null,
                            latitude: billingAddress.latitude || null,
                            longitude: billingAddress.longitude || null,
                        };
                    }
                }

                // Include business card config only when the feature is actually enabled
                if (this.form.feature_keys.includes('business-card')) {
                    const bc = this.form.businessCard;
                    cmsInput.businessCard = {
                        description: bc.description || null,
                        category: bc.category_id ? { id: bc.category_id } : null,
                        phone: bc.phone || null,
                        email: bc.email || null,
                        homepage_url: bc.homepage_url || null,
                        opening_hours: bc.opening_hours || null,
                        can_visit_location: bc.can_visit_location || false,
                        has_delivery_service: bc.has_delivery_service || false,
                    };
                    if (bc.image) {
                        cmsInput.businessCard.image = {
                            id: bc.image.id,
                            type: bc.image.type || 'business-card-image',
                            upload: bc.image.upload || false,
                            settings: bc.image.settings || null,
                        };
                    }
                    if (bc.address) {
                        cmsInput.businessCard.address = {
                            street: bc.address.street || null,
                            zipcode: bc.address.zipcode || null,
                            city: bc.address.city || null,
                            country: bc.address.country || null,
                            latitude: bc.address.latitude || null,
                            longitude: bc.address.longitude || null,
                        };
                    }
                }

                // Update mode: update existing acquisition CMS instead of creating new
                if (this.updateMode) {
                    let cms = _.removeDeepByKey(cmsInput, '__typename');
                    cms.id = this.existingCmsId;
                    const updatePayload = {
                        cms,
                        feature_keys: this.form.feature_keys.length > 0 ? this.form.feature_keys : undefined,
                    };
                    const updateResult = await WizardApi.updateCmsFromWizard(updatePayload);
                    return updateResult;
                }

                const payload = {
                    cms: cmsInput,
                    blueprint_id: this.form.blueprint_id,
                    owner_user_id: ownerUserId,
                    deep_clone: this.form.deep_clone,
                    feature_keys: this.form.feature_keys.length > 0 ? this.form.feature_keys : undefined,
                    support_tier: this.form.support_tier || undefined,
                    attach_to_shop_id:
                        this.form.shopMode === 'attach' && this.form.attach_to_shop_id
                            ? parseInt(this.form.attach_to_shop_id, 10)
                            : undefined,
                };

                // Include status and NewOwner when in super-user mode
                if (this.superUserMode) {
                    payload.status = this.form.status;
                    payload.billing_payment_method = this.form.billing_payment_method || 'stripe';
                    payload.billing_invoice_auto_send = this.form.billing_payment_method === 'invoice'
                        ? !!this.form.billing_invoice_auto_send
                        : false;

                    if (this.form.status === 'acquisition' && this.form.acquisitionOwnerMode === 'new') {
                        const newOwner = this.form.newOwner;
                        if (newOwner && newOwner.email) {
                            var ownerAddress = _.get(newOwner, 'address');
                            payload.NewOwner = {
                                email: newOwner.email,
                                first_name: newOwner.first_name || null,
                                last_name: newOwner.last_name || null,
                                company: newOwner.company || null,
                                address: ownerAddress
                                    ? {
                                          street: ownerAddress.street || null,
                                          zipcode: ownerAddress.zipcode || null,
                                          city: ownerAddress.city || null,
                                          country: ownerAddress.country || null,
                                          latitude: ownerAddress.latitude || null,
                                          longitude: ownerAddress.longitude || null,
                                      }
                                    : null,
                            };
                            // Don't send owner_user_id when creating new owner
                            delete payload.owner_user_id;
                        }
                    }
                }

                // Include Stripe payment method if collected (goes to stripe_billing_profiles)
                if (!this.form.billing.skipBilling && this.stripePaymentMethodId) {
                    payload.stripe_payment_method_id = this.stripePaymentMethodId;
                }

                const result = await WizardApi.initializeCms(payload);
                return result;
            } catch (error) {
                console.error('Failed to initialize CMS:', error);
                throw error;
            } finally {
                this.setSaving(false);
            }
        },

        resetWizard() {
            this.resetForm();
        },
    },
});
